import clsx from 'clsx';
import {
  forwardRef,
  type PropsWithChildren,
  type ReactElement,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { MultiMeterBarChartLegend } from './components/MultiMeterBarChartLegend.js';
import { MultiMeterBarChartPlotArea } from './components/MultiMeterBarChartPlotArea.js';
import { MULTIMETER_BAR_CHART_ARIA_LABEL } from './constants.js';
import { useMultiMeterBarChartConfig } from './hooks/useMultiMeterBarChartConfig.js';
import { MultiMeterBarChartProviders } from './providers/MultiMeterBarChartProviders.js';
import { Icon } from './slots/Icon.js';
import { Label } from './slots/Label.js';
import { Legend } from './slots/Legend.js';
import { Max } from './slots/Max.js';
import { Min } from './slots/Min.js';
import { Segment } from './slots/Segment.js';
import { Tooltip } from './slots/Tooltip.js';
import { Value } from './slots/Value.js';
import type { MultiMeterBarChartConsumerConfig } from './types/multi-meter-bar-chart-config.js';
import type { MultiMeterBarChartProps } from './types/multi-meter-bar-chart.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { defaultChartSize } from '../../../core/styles/defaultChartValues.sty.js';
import { ChartSeriesActionContext } from '../../core/components/series-action/ChartSeriesActionContext.js';
import { useObserveContainerSize } from '../../core/hooks/useObserveContainerSize.js';
import { ChartType } from '../../core/types/chart-types.js';
import { MeterBarChartFormattedValue } from '../shared/components/formatted-value/MeterBarChartFormattedValue.js';
import { MeterBarLayout } from '../shared/components/meter-bar-layout/MeterBarLayout.js';
import { SIZE_DEFAULT, MIN_DEFAULT_VALUE } from '../shared/constants.js';
import type { MultiMeterBarChartElementRef } from '../shared/types/meter-bars.js';

/**
 * Multi meter bar chart component
 */
const MultiMeterBarChartRoot = forwardRef<
  MultiMeterBarChartElementRef,
  PropsWithChildren<MultiMeterBarChartProps>
>((props, forwardedRef) => {
  const {
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId = 'multi-meter-bar-chart-root',
    seriesActions,
  } = props;

  const config = useMultiMeterBarChartConfig(props);

  const {
    colorPalette,
    size = SIZE_DEFAULT,
    min = MIN_DEFAULT_VALUE,
    max,
    formatter,
    iconSlot,
    labelSlot,
    valueSlot,
    minSlot,
    maxSlot,
    segments,
    legend,
    tooltip,
    totalValue,
  } = config;

  const multiMeterBarChartRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => {
    let exposedRef = {} as MultiMeterBarChartElementRef;

    if (multiMeterBarChartRef.current) {
      const consumerConfig: MultiMeterBarChartConsumerConfig = {
        colorPalette,
        size,
        min,
        max,
        formatter,
        tooltip,
        legend,
      };

      exposedRef = {
        element: multiMeterBarChartRef.current,
        getConfig: () =>
          JSON.stringify({ type: ChartType.MultiMeterBar, ...consumerConfig }),
      };
    }

    return exposedRef;
  });

  const label = labelSlot ?? '';

  const [{ width }, containerRef] = useObserveContainerSize();
  const ref = useMergeRefs([containerRef, multiMeterBarChartRef]);

  const contextSeriesActionsValue = useMemo(
    () => ({
      chartSeriesActions: seriesActions as (
        series: unknown,
        datapoint?: unknown,
      ) => ReactElement,
    }),
    [seriesActions],
  );

  return (
    <MultiMeterBarChartProviders
      colorPalette={colorPalette}
      formatter={formatter}
      segments={segments}
    >
      <ChartSeriesActionContext.Provider value={contextSeriesActionsValue}>
        <MeterBarLayout
          ref={ref}
          size={size}
          className={clsx(defaultChartSize, className)}
          style={style}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          data-testid={dataTestId}
          aria-label={MULTIMETER_BAR_CHART_ARIA_LABEL}
        >
          {iconSlot && <MeterBarLayout.Icon>{iconSlot}</MeterBarLayout.Icon>}
          {labelSlot && (
            <MeterBarLayout.Label>{labelSlot}</MeterBarLayout.Label>
          )}

          {valueSlot && (
            <MeterBarLayout.Value>
              <MeterBarChartFormattedValue
                value={totalValue}
                total={max - min}
                auto={valueSlot?.auto}
              >
                {valueSlot?.value}
              </MeterBarChartFormattedValue>
            </MeterBarLayout.Value>
          )}

          <MeterBarLayout.Chart>
            <MultiMeterBarChartPlotArea
              label={label}
              max={max}
              min={min}
              tooltipConfig={tooltip}
              width={width}
            />
          </MeterBarLayout.Chart>

          {minSlot && (
            <MeterBarLayout.Min>
              <MeterBarChartFormattedValue value={min} auto={minSlot?.auto}>
                {minSlot?.value}
              </MeterBarChartFormattedValue>
            </MeterBarLayout.Min>
          )}

          {maxSlot && (
            <MeterBarLayout.Max>
              <MeterBarChartFormattedValue value={max} auto={maxSlot?.auto}>
                {maxSlot?.value}
              </MeterBarChartFormattedValue>
            </MeterBarLayout.Max>
          )}

          {legend && (
            <MeterBarLayout.Legend>
              <MultiMeterBarChartLegend width={width} />
            </MeterBarLayout.Legend>
          )}
        </MeterBarLayout>
      </ChartSeriesActionContext.Provider>
    </MultiMeterBarChartProviders>
  );
});

(
  MultiMeterBarChartRoot as typeof MultiMeterBarChartRoot & {
    displayName: string;
  }
).displayName = '_MultiMeterBarChart';

/**
 * The `MultiMeterBarChart` is a more complex version of the single meter bar,
 * it allows for different data inputs to be considered when presenting progress
 * toward a goal, consumption of a total, etc.
 * @public
 */
export const MultiMeterBarChart = Object.assign(MultiMeterBarChartRoot, {
  Icon,
  Label,
  Legend,
  Max,
  Min,
  Segment,
  Value,
  Tooltip,
});
